import { Action } from "@opstel/query/v2/models/action";
import { ActionType } from "@opstel/query/v2/models/actionType";
import { OccupationInteraction, TrackOccupation } from "@opstel/query/models/trackOccupation";
import React from "react";
import { NoIcon } from "./NoIcon";
import { ElementOf } from "@shared/common/types/typeHelpers";

interface IconProps {
  readonly color?: string;
  readonly action: Action;
  readonly occupation: TrackOccupation;
}
type IconComponent = (props: IconProps) => React.JSX.Element | null;
type ActionIconMap = { [key in ActionType]: IconComponent };

export const actionIconMap: ActionIconMap = {
  Combine: ({ color }: IconProps) => (
    <tractie-icon name="combine" size="xs" style={{ transform: "rotate(90deg)" }} color={color} />
  ),
  Split: ({ color }: IconProps) => (
    <tractie-icon name="divide" size="xs" style={{ transform: "rotate(90deg)" }} color={color} />
  ),
  Move: ({ action, occupation, color }: IconProps) => {
    const moveIconName = determineMovementIcon(action, occupation);
    if (!moveIconName) return <NoIcon />;
    return <tractie-icon name={moveIconName} size="xs" color={color} />;
  },
  Wait: NoIcon,
  Enter: NoIcon,
  Exit: NoIcon,
  AtBegin: NoIcon,
  AtEnd: NoIcon,
  Decloak: NoIcon,
  Cloak: NoIcon,
  Teleport: NoIcon,
  Filler: NoIcon,
  WrapperCombine: NoIcon,
  WrapperSplit: NoIcon,
  Wrapper: NoIcon,
};

export function hasIcon(actionType: ActionType): boolean {
  return actionIconMap[actionType] !== NoIcon;
}

const movementInteractions = ["MoveTargetSide", "MoveSourceSide"] as const;
type MovementInteraction = ElementOf<typeof movementInteractions>;
const originatesFromToIconName: Record<MovementInteraction, string> = {
  MoveTargetSide: "arrow-top",
  MoveSourceSide: "arrow-bottom",
};
const dissipatesIntoToIconName: Record<MovementInteraction, string> = {
  MoveTargetSide: "arrow-bottom",
  MoveSourceSide: "arrow-top",
};
function isMovementInteraction(
  interaction: OccupationInteraction,
): interaction is MovementInteraction {
  return (movementInteractions as readonly OccupationInteraction[]).includes(interaction);
}
function determineMovementIcon(action: Action, occupation: TrackOccupation): string | null {
  if (!occupation.actions) return null;
  if (action.id === occupation.actions[0].id && isMovementInteraction(occupation.originatesFrom)) {
    return originatesFromToIconName[occupation.originatesFrom];
  } else if (
    action.id === occupation.actions.at(-1)!.id &&
    isMovementInteraction(occupation.dissipatesInto)
  ) {
    return dissipatesIntoToIconName[occupation.dissipatesInto];
  }
  return null;
}
